import { TeacherMetricCard, TeacherSectionHeader, TeacherStudentIdentity, TeacherAssessmentHistoryRow, TeacherDrawerShell, TeacherDrawerNav } from '../../design/TeacherComponents';
import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { classPulseDate, classPulseFormat } from './classPulseMetrics';
import { CourseGlyph, getCourseVisual } from './ClassPulseVisualTokens';
import { teacherTypography, teacherChartText } from '../../design/typography';
import { developmentLevelLabel } from '../../shared/developmentLevels';

const actionFocus = {
  minHeight: 44, minWidth: 44,
  '&.Mui-focusVisible': { outline: '3px solid #302479', outlineOffset: 2 },
};
const validDate = (date) => /^\d{4}-\d{2}-\d{2}$/.test(String(date || '').slice(0, 10));

/**
 * Multiple assessments can share a calendar date. Consolidate those into one
 * date-average point rather than silently overwriting an assessment's score.
 * The original records remain separately available in the results list.
 */
export const courseHistoryPoints = (course) => {
  const byDay = new Map();
  (course?.history || []).forEach(record => {
    if (!validDate(record.date) || !Number.isFinite(record.score)) return;
    const date = record.date.slice(0, 10);
    const bucket = byDay.get(date) || [];
    bucket.push(record.score);
    byDay.set(date, bucket);
  });
  return [...byDay.entries()].sort(([left], [right]) => left.localeCompare(right))
    .map(([date, scores]) => ({
      date, score: scores.reduce((sum, score) => sum + score, 0) / scores.length,
      count: scores.length,
    }));
};

export const recordsForCourse = (student, key) => (
  (student?.records || []).filter(record => record.subjectKey === key)
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))
);

/** Include lessons with only pending/absent results without inventing a zero. */
export const studentCourseOptions = (student) => {
  const byKey = new Map((student?.courses || []).map(course => [course.key, course]));
  (student?.records || []).forEach(record => {
    if (!record.subjectKey || record.subjectKey === 'unassigned' || byKey.has(record.subjectKey)) return;
    byKey.set(record.subjectKey, {
      key: record.subjectKey, label: record.subject, average: null, count: 0, history: [],
    });
  });
  return [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label, 'tr-TR'));
};

function CourseHistoryChart({ course }) {
  const points = useMemo(() => courseHistoryPoints(course), [course]);
  if (points.length < 2) {
    return (
      <Typography variant="body2" sx={{ ...teacherTypography.supportingText, py: 2 }}>
        {points.length === 1
          ? 'Bu derste henüz tek tarihli sonuç var. İlerlemeyi karşılaştırmak için yeni sonuçlar gerekli.'
          : 'Grafik için bu derste tarihli, puanlı en az iki değerlendirme gerekli.'}
      </Typography>
    );
  }
  const plotted = points.slice(-8);
  const x = index => 46 + (index * 500 / (plotted.length - 1));
  const y = score => 150 - score * 1.12;
  const line = plotted.map((p, i) => `${x(i)},${y(p.score)}`).join(' ');
  const area = `M ${x(0)} ${y(0)} L ${line.replaceAll(' ', ' L ')} L ${x(plotted.length - 1)} 150 L ${x(0)} 150 Z`;
  return (
    <Box>
      <Box sx={{ width: '100%', minWidth: 0, ...teacherChartText }}>
        <svg role="img" viewBox="0 0 600 193" style={{ width: '100%', display: 'block', height: 'auto' }}
          aria-label={`${course.label} geçmiş puanları: ${plotted.map(p => `${classPulseDate(p.date)} ${classPulseFormat(p.score)}`).join(', ')}`}>
          <defs>
            <linearGradient id="danduCourseHistoryArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#7860da" stopOpacity="0.23" />
              <stop offset="100%" stopColor="#7860da" stopOpacity="0.015" />
            </linearGradient>
          </defs>
          {[0, 25, 50, 75, 100].map(value => (
            <g key={value}>
              <line x1="37" x2="562" y1={y(value)} y2={y(value)} stroke="#e6e8f0" strokeDasharray="5 5" />
              <text className="dandu-chart-axis" x="4" y={y(value) + 4}>{value}</text>
            </g>
          ))}
          <path data-testid="course-chart-fill" d={area} fill="url(#danduCourseHistoryArea)" stroke="none" />
          <polyline points={line} stroke="#7458d6" strokeWidth="3" fill="none"
            strokeLinecap="round" strokeLinejoin="round" />
          {plotted.map((point, i) => (
            <g key={point.date}>
              <circle cx={x(i)} cy={y(point.score)} r="5" stroke="#7458d6" strokeWidth="3" fill="white" />
              <text className="dandu-chart-score" x={x(i)} y={Math.max(12, y(point.score) - 13)}
                textAnchor="middle">{classPulseFormat(point.score)}</text>
              <text className={plotted.length > 4 && i % 2 === 1
                ? 'dandu-chart-date dandu-chart-date-optional' : 'dandu-chart-date'}
                x={x(i)} y="179" textAnchor="middle">
                {point.date.slice(5).split('-').reverse().join('.')}
              </text>
            </g>
          ))}
        </svg>
      </Box>
      <Typography variant="caption" sx={{ ...teacherTypography.metadata, display: 'block', mt: 0.5, lineHeight: 1.65 }}>
        {points.length < 3
          ? 'İki tarihli puan gösteriliyor; gelişim eğilimi hakkında yorum yapılmaz.'
          : 'Grafik öğrencinin kendi puan geçmişini gösterir. Farklı sınavların zorlukları karşılaştırılabilir olmayabilir.'}
        {points.length > 8 ? ' Grafikte son sekiz tarih yer alır; tüm sonuçlar aşağıdadır.' : ''}
      </Typography>
    </Box>
  );
}

function StudentInsight({ student }) {
  const sorted = [...student.courses].sort((a, b) => b.average - a.average);
  const strongest = sorted[0];
  const weakest = sorted[sorted.length - 1];
  const enough = sorted.length >= 2 && strongest.count >= 2 && weakest.count >= 2;
  const opportunity = enough && strongest.average - weakest.average >= 15;
  const documentedHistory = student.courses.some(course => courseHistoryPoints(course).length >= 3);
  const statements = [];
  if (enough) statements.push(`${strongest.label}, puanlarına göre güçlü görünen derslerinden biri.`);
  if (opportunity) statements.push(`${weakest.label} için pekiştirme fırsatı var.`);
  if (documentedHistory) statements.push('Ders seçerek önceki değerlendirme puanlarını inceleyebilirsiniz.');
  if (!statements.length) statements.push('Daha güvenilir bir ders profili için ek değerlendirme sonuçları gerekiyor.');
  return (
    <Paper variant="outlined" sx={{ p: 2.2, mt: 2, borderRadius: 3.5,
      background: 'linear-gradient(110deg, #f5f0ff, #fcfaff)', borderColor: '#e1d6ff' }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <AutoAwesomeOutlinedIcon color="secondary" fontSize="small" sx={{ mt: 0.2 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600}>Öğretmen için kısa bakış</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.65 }}>{statements.join(' ')}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function SelectedCourseDetails({ course, student }) {
  const results = useMemo(() => recordsForCourse(student, course.key), [student, course.key]);
  const valid = results.filter(record => record.score !== null && record.score !== undefined).length;
  const developmentCount = results.filter(record => record.developmentLevel).length;
  const categoricalOnly = valid === 0 && developmentCount > 0;
  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2.5 }, mt: 2, borderRadius: 3.5,
      borderColor: '#e5e7f2', boxShadow: '0 8px 28px rgba(60,50,120,.035)' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <CourseGlyph name={course.label} size={34} />
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h3" variant="subtitle1" sx={teacherTypography.sectionTitle}>
              {course.label} · {categoricalOnly ? 'Gelişim Düzeyi Geçmişi' : 'Puan Geçmişi'}
            </Typography>
            <Typography variant="caption" sx={teacherTypography.metadata}>
              {valid} puanlı değerlendirme · {developmentCount} gelişim düzeyi · {results.length} toplam kayıt
            </Typography>
          </Box>
        </Stack>
        <Chip label="Ders detayı" size="small" sx={{
          bgcolor: '#f2edff', color: '#5941a7', fontWeight: 600, height: 28,
        }} />
      </Stack>
      {categoricalOnly ? (
        <Typography variant="body2" sx={{ ...teacherTypography.supportingText, py: 2 }}>
          Bu dersteki sonuçlar kategorik Gelişim Düzeyi değerlendirmeleridir. Sayısal ortalama ve puan grafiği oluşturulmaz.
        </Typography>
      ) : <CourseHistoryChart course={course} />}
      <Divider sx={{ my: 2.5 }} />
      <Typography component="h3" variant="subtitle1" sx={{ ...teacherTypography.sectionTitle, mb: 1 }}>
        {course.label} · Değerlendirme Sonuçları
      </Typography>
      <Stack spacing={0}>
        {results.map(record => (
          <TeacherAssessmentHistoryRow key={record.id} title={record.title} dateLabel={classPulseDate(record.date)}
            value={record.status === 'completed' && !record.developmentLevel ? record.score : null} label="/ 100"
            summary={record.developmentLevel ? developmentLevelLabel(record.developmentLevel) : record.status === 'absent' ? 'Girmedi' : record.status === 'pending' ? 'Sonuç bekleniyor' : null} />
        ))}
      </Stack>
      {!results.length && (
        <Typography variant="body2" sx={{ ...teacherTypography.supportingText, py: 2 }}>
          Bu ders için henüz kayıt bulunmuyor.
        </Typography>
      )}
    </Paper>
  );
}

export default function ClassPulseStudentDrawer({ student, students = [], onClose, onSelect, className }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedCourseKey, setSelectedCourseKey] = useState(null);
  const index = student ? students.findIndex(item => item.id === student.id) : -1;
  const courses = useMemo(() => studentCourseOptions(student), [student]);
  const unassignedRecords = useMemo(() => recordsForCourse(student, 'unassigned'), [student]);
  useEffect(() => { setSelectedCourseKey(null); }, [student?.id]);
  const selectedCourse = useMemo(() => (
    courses.find(course => course.key === selectedCourseKey) || courses[0] || null
  ), [courses, selectedCourseKey]);

  return (
    <TeacherDrawerShell open={Boolean(student)} onClose={onClose}
      label={student ? `${student.name} öğrenci değerlendirme detayı` : undefined}>
      {student && (
        <Box sx={{ p: { xs: 2, sm: 3 }, pb: 4, minHeight: '100%',
          overflowY: 'auto', minWidth: 0 }}>
          <TeacherDrawerNav onClose={onClose} back={fullScreen} label={fullScreen ? 'Öğrenci listesine geri dön' : 'Öğrenci detayını kapat'} context={className + ' · Öğrenci detayı'}>
            {!fullScreen && <Stack direction="row" spacing={0.5}>
              <IconButton aria-label="Önceki öğrenci" disabled={index <= 0} sx={actionFocus} onClick={() => onSelect(students[index - 1]?.id)}><ChevronLeftIcon /></IconButton>
              <IconButton aria-label="Sonraki öğrenci" disabled={index < 0 || index >= students.length - 1} sx={actionFocus} onClick={() => onSelect(students[index + 1]?.id)}><ChevronRightIcon /></IconButton>
            </Stack>}
          </TeacherDrawerNav>
          <Box sx={{ mb: 2 }}><TeacherStudentIdentity student={student} metadata={className} detail /></Box>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
            <TeacherMetricCard density="report" label="Genel ortalama · Seçili dönem" emphasis
              value={student.average === null && student.developmentCount ? 'Gelişim Düzeyi' : classPulseFormat(student.average)}
              unit={student.average !== null ? '/ 100' : null}
              help={student.subjectCount + ' ders' + (student.unassigned ? ' · ' + student.unassigned.count + ' ders atanmamış sonuç' : '')} />
            <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3.5, bgcolor: '#f8faff',
              borderColor: '#e4e9f5', minWidth: 0, flex: 1.4 }}>
              <Stack direction="row" spacing={0.8} alignItems="center">
                <InfoOutlinedIcon fontSize="small" color="info" />
                <Typography variant="subtitle2" fontWeight={600}>Ortalama nasıl hesaplanır?</Typography>
              </Stack>
              <Typography variant="caption" sx={{ ...teacherTypography.metadata, display: 'block', mt: 0.8, lineHeight: 1.65 }}>
                Her dersin puanlı sonuçlarının ortalaması alınır. Ders ortalamaları ve varsa ders atanmamış sonuç grubu eşit ağırlıkla genel ortalamaya katılır. Girmedi ve bekleyen sonuçlar puan sayılmaz.
              </Typography>
            </Paper>
          </Stack>

          <TeacherSectionHeader level="h3" title="Ders Bazlı Performans
          " />
          {courses.length ? (
            <Box sx={{ display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))', md: `repeat(${Math.min(4, courses.length)},minmax(0,1fr))` },
              gap: 1.2 }}>
              {courses.map((course) => {
                const active = selectedCourse?.key === course.key;
                const visual = getCourseVisual(course.label);
                const hasNumericAverage = Number.isFinite(course.average);
                const hasDevelopmentResults = student.records.some(r => r.subjectKey === course.key && r.developmentLevel);
                return (
                  <Button key={course.key} variant="outlined" color="inherit"
                    aria-label={`${course.label} dersinin sonuçlarını göster`}
                    aria-pressed={active} onClick={() => setSelectedCourseKey(course.key)}
                    sx={{ ...actionFocus, display: 'block', p: { xs: 1.25, sm: 1.5 },
                      textAlign: 'left', minWidth: 0, minHeight: { xs: 85, sm: 151 },
                      textTransform: 'none', borderRadius: 3, borderWidth: active ? 2 : 1,
                      borderColor: active ? '#7458d6' : '#e5e7f2',
                      background: active ? 'linear-gradient(115deg,#f5f0ff,#fff)' : '#fff',
                      boxShadow: active ? '0 5px 17px rgba(111,81,202,.09)' : 'none',
                      '&:hover': { borderColor: '#7458d6', bgcolor: '#faf8ff' } }}>
                    <Box sx={{ display: { xs: 'grid', sm: 'block' },
                      gridTemplateColumns: { xs: 'minmax(0,1fr) auto' }, columnGap: 1,
                      alignItems: 'center', minWidth: 0 }}>
                      <Stack direction="row" spacing={0.9} alignItems="center"
                        sx={{ minWidth: 0, gridColumn: { xs: 1, sm: 'auto' } }}>
                        <CourseGlyph name={course.label} size={34} />
                        <Typography variant="body2" sx={{ ...teacherTypography.cardTitle,
                          overflowWrap: 'anywhere', color: '#18213d', minWidth: 0,
                        }}>
                          {course.label}
                        </Typography>
                      </Stack>
                      <Typography variant="h5" component="span" sx={{
                        display: 'block', my: { xs: 0, sm: 0.8 }, textAlign: { xs: 'right', sm: 'left' },
                        fontSize: { xs: '1.1rem', sm: '1.5rem' },
                        gridColumn: { xs: 2, sm: 'auto' }, gridRow: { xs: 1, sm: 'auto' },
                        fontVariantNumeric: 'tabular-nums', color: '#1d2450', fontWeight: 700 }}>
                        {classPulseFormat(course.average)}
                        {hasNumericAverage && (
                          <Typography component="span" variant="caption" sx={teacherTypography.scoreUnit}> /100</Typography>
                        )}
                      </Typography>
                      <Typography variant="caption" sx={{ ...teacherTypography.metadata,
                        display: 'block', mt: { xs: 0.15, sm: 0 }, mb: { xs: 0.5, sm: 0.8 },
                        gridColumn: { xs: '1 / -1', sm: 'auto' },
                      }}>
                        {course.count ? `${course.count} puanlı değerlendirme` : hasDevelopmentResults ? 'Kategorik değerlendirme · Sayısal ortalama yok.' : 'Puanlı sonuç bekleniyor'}
                      </Typography>
                      {hasNumericAverage && <Box role="progressbar" aria-label={`${course.label} sayısal puan ortalaması`}
                        aria-valuenow={course.average} aria-valuemin={0} aria-valuemax={100}
                        sx={{ bgcolor: '#eceaf3', height: 7, borderRadius: 5, overflow: 'hidden',
                        gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
                        <Box sx={{ bgcolor: visual.line, height: '100%',
                          width: `${Math.max(0, Math.min(100, course.average))}%`, borderRadius: 5 }} />
                      </Box>}
                    </Box>
                  </Button>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" sx={teacherTypography.supportingText}>Henüz puanı girilmiş ders bulunmuyor.</Typography>
          )}

          {selectedCourse && <SelectedCourseDetails key={`${student.id}:${selectedCourse.key}`}
            course={selectedCourse} student={student} />}
          <StudentInsight student={student} />

          {(student.unassigned || unassignedRecords.length > 0) && (
            <Paper component="section" aria-label="Ders atanmamış değerlendirme geçmişi"
              variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 3, bgcolor: '#f8f9fd' }}>
              <Typography variant="subtitle2" fontWeight={600}>Ders atanmamış değerlendirmeler</Typography>
              {student.unassigned && <Typography variant="body2" sx={{ ...teacherTypography.supportingText, mt: 0.5, lineHeight: 1.65 }}>
                {student.unassigned.count} puanlı sonuç · ortalama {classPulseFormat(student.unassigned.average)}/100.
                Bu kayıtlar genel ortalamaya ayrı bir grup olarak katılır, ders sayısına dahil edilmez.
              </Typography>}
              {unassignedRecords.some(record => record.scoringMode === 'development') && (
                <Typography variant="body2" sx={{ ...teacherTypography.supportingText, mt: 0.5, lineHeight: 1.65 }}>
                  Gelişim Düzeyi sonuçları kategorik değerlendirmelerdir; sayısal ortalamaya ve puan grafiklerine katılmaz.
                </Typography>
              )}
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {unassignedRecords.map(record => (
                  <Stack component="li" data-testid="unassigned-assessment-result" key={record.id}
                    direction="row" justifyContent="space-between" alignItems="center" spacing={1}
                    sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>{record.title}</Typography>
                      <Typography variant="caption" sx={teacherTypography.metadata}>{classPulseDate(record.date)}</Typography>
                    </Box>
                    <Typography variant="caption" sx={{ ...teacherTypography.metadata, flexShrink: 0 }}>
                      {record.status === 'absent' ? 'Girmedi' :
                        record.status === 'pending' ? 'Sonuç bekleniyor' :
                          record.developmentLevel ? developmentLevelLabel(record.developmentLevel) : `${classPulseFormat(record.score)} /100`}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            </Paper>
          )}
          {fullScreen && (
            <Button variant="outlined" fullWidth onClick={onClose} sx={{ ...actionFocus, mt: 3, minHeight: 48 }}>
              Öğrenci listesine dön
            </Button>
          )}
        </Box>
      )}
    </TeacherDrawerShell>
  );
}
